<template>
  <v-card class="feed-card">
    <v-row>
      <v-col cols="12" sm="5" class="dog-photo-col">
        <v-img
          class="ma-auto rounded grey lighten-4"
          :aspect-ratio="1"
          :contain="!dogInfo.photoUrl"
          :class="dogInfo.photoUrl ? 'enlargeable' : 'pa-6'"
          :src="dogInfo.photoUrl || defaultImage"
          @click.native="dogInfo.photoUrl && (photoDialog = true)"
        />
        <photo-lightbox v-model="photoDialog" :src="dogInfo.photoUrl" />
        <div v-if="dogInfo.currentUserIsCoowner" class="text-center mt-2">
          <input
            ref="photoInput"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            class="d-none"
            @change="handlePhotoSelected"
          />
          <v-btn small text color="primary" :loading="photoSaving" @click="photoTermsDialog = true">
            {{ dogInfo.photoUrl ? "Change photo" : "Add photo" }}
          </v-btn>
          <photo-terms-dialog
            v-model="photoTermsDialog"
            :has-photo="!!dogInfo.photoUrl"
            :uploading="photoSaving"
            :error="photoError"
            @choose="choosePhoto"
            @clear-error="photoError = ''"
          />
          <v-btn
            v-if="dogInfo.photoUrl"
            small
            text
            color="red"
            :disabled="photoSaving"
            @click="removePhoto"
          >
            Remove
          </v-btn>
          <div v-if="photoError" class="red--text caption mt-1">{{ photoError }}</div>
        </div>
      </v-col>
      <v-col cols="12" sm="7">
        <p>
          <b>NAME: </b>
          <router-link :to="`/dogs/${encodeURIComponent(dogInfo.dogId)}`">{{
            dogInfo.name
          }}</router-link
          ><br />
          <b>DOG ID: </b> {{ dogInfo.dogId }}<br />
          <span v-if="dogInfo.currentUserIsCoowner"><b>CHIP #: </b> {{ dogInfo.chipNumber }}<br /></span>
          <b>GENDER: </b> {{ dogInfo.gender }}<br />
          <b>AGE: </b> {{ dogInfo.age }} years<br />
          <b>BIRTHDAY: </b> <localized-date v-if="dogInfo.birthdate" :date="dogInfo.birthdate" /> <br />
          <b>SIRE: </b> {{ dogInfo.sire }}<br />
          <b>DAM: </b> {{ dogInfo.dam }}<br />
          <span v-if="dogInfo.currentUserIsCoowner"><b>LITTER #: </b> {{ dogInfo.litterId }}<br /></span>
          <CoownersList
            :owners="dogInfo.owners"
            :ownershipType="dogOwnershipType"
          />
        </p>
      </v-col>
    </v-row>
    <v-row>
        <v-col>
        <router-link
            class="no-text-decoration"
            :to="`/registration/litter/${dogInfo.dogId}`"
        >
            <v-btn
            light
            large
            color="primary"
            width="135px"
            class="mr-5 mt-5"
            v-if="dogInfo.canRegisterLitter"
            >
            Register Litter
            </v-btn>
        </router-link>
        <router-link
            class="no-text-decoration"
            :to="`/leaserequest/${dogInfo.dogId}`"
        >
            <v-btn 
            light
            large
            color="primary"
            width="135px"
            class="mr-5 mt-5"
            v-if="dogInfo.canRegisterLease"> Add Lease </v-btn>
        </router-link>
        </v-col>
    </v-row>
  </v-card>
</template>

<script lang="ts">
import Vue from "vue";
import CoownersList, { OwnershipType } from "@/components/common/CoownersList.vue";
import PhotoLightbox from "@/components/common/PhotoLightbox.vue";
import PhotoTermsDialog, { PHOTO_TERMS_VERSION } from "@/components/common/PhotoTermsDialog.vue";
import { UserInfo } from "@/types/User";
import dogService from "@/services/dogService";
import { resizeImage } from "@/helpers/resize-image";
import { getErrorMessage } from "@/services/errorHandler";

const defaultImage = require('@/assets/logo.svg');

export default Vue.extend({
  name: "DogCard",
  data: function () {
    return {
      dogInfo: {} as any,
      defaultImage,
      photoSaving: false,
      photoTermsDialog: false,
      photoDialog: false,
      photoError: "",
    };
  },
  components: {
    CoownersList,
    PhotoLightbox,
    PhotoTermsDialog,
  },
  props: {
    selectedDog: String,
    initialDogInfo: Object,
  },
  created: function () {
    this.loadSelectedDog();
  },
  methods: {
    loadSelectedDog: function () {
      if (!this.selectedDog || !this.userInfo) return;

      if (this.initialDogInfo?.dogId?.trim() === this.selectedDog.trim()) {
        this.dogInfo = this.initialDogInfo;
        return;
      }

      dogService
        .getDogById(this.selectedDog)
        .then((res) => {
          this.dogInfo = res;
        })
        .catch(this.$handleError);
    },
    reloadDog: function (): Promise<void> {
      return dogService
        .getDogById(this.selectedDog)
        .then((res) => {
          this.dogInfo = res;
        });
    },
    choosePhoto: function () {
      this.photoError = "";
      (this.$refs.photoInput as HTMLInputElement).click();
    },
    handlePhotoSelected: async function (event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files && input.files[0];
      input.value = "";

      if (!file) return;

      this.photoSaving = true;
      this.photoError = "";
      try {
        const resized = await resizeImage(file);
        const res = await dogService.uploadDogPhoto(this.dogInfo.dogId, resized, PHOTO_TERMS_VERSION);

        if (res.success) {
          this.photoTermsDialog = false;
          await this.reloadDog();
          this.$emit("photo-changed", { dogId: this.dogInfo.dogId, photoUrl: this.dogInfo.photoUrl });
        } else {
          this.photoError = res.errorMessage;
        }
      } catch (e: any) {
        this.photoError = getErrorMessage(e);
      } finally {
        this.photoSaving = false;
      }
    },
    removePhoto: async function () {
      this.photoSaving = true;
      try {
        const res = await dogService.deleteDogPhoto(this.dogInfo.dogId);

        if (res.success) {
          await this.reloadDog();
          this.$emit("photo-changed", { dogId: this.dogInfo.dogId, photoUrl: this.dogInfo.photoUrl });
        } else {
          this.photoError = res.errorMessage;
        }
      } finally {
        this.photoSaving = false;
      }
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    dogOwnershipType: function () {
      return OwnershipType.Dog;
    },
  },
  watch: {
    selectedDog: function () {
      this.$nextTick(this.loadSelectedDog);
    },
    initialDogInfo: function (newVal: any) {
      if (newVal?.dogId?.trim() === this.selectedDog?.trim()) {
        this.dogInfo = newVal;
      }
    },
  },
});
</script>

<style scoped>
/* Stacked on a phone, the photo column is full width; cap it so the picture does not
   push the dog's details off the screen. */
@media (max-width: 599px) {
  .dog-photo-col >>> .v-image {
    max-width: 260px;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
